<template>
	<Transition name="modal">
		<div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
			<div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
				<div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-opacity-50" @click="$emit('close')"></div>

				<div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-2xl sm:w-full flex flex-col sm:block overflow-hidden">
					<div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between shrink-0">
						<h3 class="text-lg leading-6 font-medium text-white">Employee Details</h3>
						<button @click="$emit('close')" class="text-white/80 hover:text-white transition">
						<X class="h-5 w-5" />
						</button>
					</div>

					<div class="px-4 py-5 sm:p-6 overflow-y-auto flex-1 sm:flex-none max-h-[calc(100vh-10rem)] sm:max-h-none sm:h-[740px]">
						<div v-if="loading" class="flex justify-center py-10">
							<Loader2 class="h-8 w-8 animate-spin text-primary" />
						</div>

						<div v-else-if="employee" class="space-y-6">
							<div class="flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
								<template v-if="shouldShowProfileImage">
									<img
										:src="profileImageUrl"
										:alt="`${fullName} photo`"
										class="h-16 w-16 rounded-full object-cover shadow-md flex-shrink-0"
										@error="onProfileImageError"
									/>
								</template>
								<div
									v-else
									class="h-16 w-16 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0"
									:class="avatarBgClass"
								>
									{{ initials }}
								</div>
								<div>
									<h2 class="text-xl font-bold text-slate-800 dark:text-white">{{ fullName }}</h2>
									<p class="text-sm text-slate-500 dark:text-slate-400">{{ employee.email }}</p>
								</div>
								<div class="ml-auto">
									<StatusBadge :status="employee.status" />
								</div>
							</div>

							<div class="space-y-6 mt-6">
								<!-- Personal Info -->
								<div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
									<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
										<User class="h-4 w-4 inline" /> Personal Information
									</h4>
									<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
										<DetailField label="First Name" :value="employee.first_name" />
										<DetailField label="Last Name" :value="employee.last_name" />
										<DetailField label="Date of Birth" :value="employee.date_of_birth" />
										<DetailField label="Gender" :value="employee.gender" capitalize />
										<DetailField label="Phone" :value="employee.phone" />
									</div>
								</div>

								<!-- Address -->
								<div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
									<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
										<MapPinHouse class="h-4 w-4 inline" /> Address
									</h4>
									<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
										<div class="md:col-span-2">
										<DetailField label="Street" :value="employee.address" />
										</div>
										<DetailField label="City" :value="employee.city" />
										<DetailField label="State" :value="employee.state" />
										<DetailField label="Zipcode" :value="employee.zipcode" />
									</div>
								</div>

								<!-- Account Info -->
								<div class="bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600">
									<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
										<FileUser class="h-4 w-4 inline" /> Account Information
									</h4>
									<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
										<DetailField label="Position" :value="employee.position || employee.position_id?.toString()" />
										<DetailField label="Email" :value="employee.email" />
										<DetailField label="Username" :value="employee.username" />
										<div>
											<DetailField label="Password" :value="showViewPassword ? (employee.visible_password || '') : '********'" />
											<p class="text-[11px] text-primary/80 mt-1 font-medium">
												Provided to employee
												<button type="button" @click="showViewPassword = !showViewPassword" class="inline-flex items-center ml-1 text-slate-400 hover:text-primary transition-colors align-middle">
													<component :is="showViewPassword ? EyeOff : Eye" class="h-3.5 w-3.5" />
												</button>
											</p>
										</div>
										<DetailField label="Date Created" :value="formatDate(employee.created_at)" />
										<DetailField label="Last Updated" :value="formatDate(employee.updated_at)" />
									</div>
								</div>
							</div>
						</div>

						<div v-else class="text-center py-10 text-slate-500">
						No employee data available.
						</div>
					</div>

					<div class="bg-gray-50 dark:bg-gray-700 px-4 py-4 flex flex-col gap-3 sm:px-6 sm:flex-row-reverse sm:gap-3 shrink-0">
						<button @click="$emit('edit', employee?.id!)" class="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-secondary transition">
							<Pencil class="h-4 w-4 inline mr-1" /> Edit
						</button>
						<button @click="$emit('close')" class="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition">
						Close
						</button>
					</div>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import StatusBadge from './StatusBadge.vue'
import DetailField from './DetailField.vue'
import type { Employee } from '@/services/employeeService'
import { Loader2, X, Pencil, User, MapPinHouse, FileUser, Eye, EyeOff } from 'lucide-vue-next'
import api from '@/services/api'

const props = defineProps<{
	isOpen: boolean
	employee: Employee | null
	loading?: boolean
}>()

defineEmits<{
	(e: 'close'): void
	(e: 'edit', id: number): void
}>()

const avatarLoadFailed = ref(false)
const showViewPassword = ref(false)

const fullName = computed(() => {
	if (props.employee?.first_name || props.employee?.last_name) {
		return `${props.employee.first_name || ''} ${props.employee.last_name || ''}`.trim()
	}
	return props.employee?.name || 'N/A'
})

const initials = computed(() => {
	const name = fullName.value
	return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
})

const profileImageUrl = computed(() => {
	const raw = String(props.employee?.profile_picture || '').trim()
	if (!raw || raw.toLowerCase() === 'null') return ''
	if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw
	const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
	const baseNoApi = rawBase.replace(/\/api$/i, '')
	if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
	if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`
	return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
})

const shouldShowProfileImage = computed(() => Boolean(profileImageUrl.value) && !avatarLoadFailed.value)

watch(profileImageUrl, () => {
  avatarLoadFailed.value = false
})

const onProfileImageError = () => {
  avatarLoadFailed.value = true
}

const avatarBgClass = computed(() => {
	const gradients = [
		'from-primary to-secondary',
		'from-success to-success-hover',
		'from-info to-info-hover',
		'from-warning to-warning-hover',
		'from-danger to-danger-hover',
	]
	const name = fullName.value || 'user'
	let hash = 0
	for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
	return gradients[Math.abs(hash) % gradients.length]
})

const formatDate = (dateStr: string) => {
	if (!dateStr) return 'N/A'
	return new Date(dateStr).toLocaleDateString('en-US', {
		year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
	})
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  	transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  	opacity: 0;
}
</style>
